/**
 * Support Tickets Route Loaders
 */

import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'
import { parseUrlFilter } from '@spcc/core'
import { supportApi } from '../api/support-api'
import { supportActions } from '../../store/support.slice'
import type { SupportTicketsFilter } from '../../domain/types'
import type { SupportTicketsSearchParams } from '../../domain/filter-config'
import { supportFilterConfig } from '../../domain/filter-config'

interface RouterContext {
  store: {
    dispatch: Dispatch<UnknownAction>
    getState: () => unknown
  }
}

interface LoaderParams {
  context: RouterContext
  initialSearch?: SupportTicketsSearchParams
}

function parseInitialFilter(search?: SupportTicketsSearchParams): SupportTicketsFilter {
  if (!search) {
    return supportFilterConfig.defaults
  }

  const params: Record<string, string | null> = {}
  for (const [key, value] of Object.entries(search)) {
    params[key] = value ?? null
  }

  return (parseUrlFilter(params, supportFilterConfig as any) as unknown) as SupportTicketsFilter
}

export const customerSupportLoader = async ({ context }: Pick<LoaderParams, 'context'>) => {
  if (!context?.store) {
    console.error('[customerSupportLoader] Store not available in router context')
    return null
  }

  const { store } = context
  const customerFilter: SupportTicketsFilter = {
    ...supportFilterConfig.defaults,
    channel: 'customer_to_spcc',
  }

  try {
    store.dispatch(supportActions.setLoading(true))
    store.dispatch(supportActions.setFilter(customerFilter))

    const tickets = await supportApi.getTickets(customerFilter)
    store.dispatch(supportActions.fetchTicketsSuccess(tickets))

    return null
  } catch (error) {
    console.error('[customerSupportLoader] Error loading tickets:', error)
    store.dispatch(supportActions.setError((error as Error).message))
    return null
  }
}

export const supportTicketsLoader = async ({ context, initialSearch }: LoaderParams) => {
  if (!context?.store) {
    console.error('[supportTicketsLoader] Store not available in router context')
    return null
  }

  const { store } = context
  const initialFilter = parseInitialFilter(initialSearch)

  try {
    store.dispatch(supportActions.setLoading(true))
    store.dispatch(supportActions.setFilter(initialFilter))

    const [tickets, stats] = await Promise.all([
      supportApi.getTickets(initialFilter),
      supportApi.getTicketStats({ channel: initialFilter.channel }),
    ])

    store.dispatch(supportActions.fetchTicketsSuccess(tickets))
    store.dispatch(supportActions.setStats(stats))

    return null
  } catch (error) {
    console.error('[supportTicketsLoader] Error loading tickets:', error)
    store.dispatch(supportActions.setError((error as Error).message))
    return null
  }
}
